/**
 * Anthropic SDK adapter for codebase_search tool
 */

import type { Tool } from '@anthropic-ai/sdk/resources/messages';
import { executeCodebaseSearch } from './core.js';
import { CODEBASE_SEARCH_DESCRIPTION, CODEBASE_SEARCH_SYSTEM_PROMPT } from './prompts.js';
import type { CodebaseSearchConfig, CodebaseSearchInput, CodebaseSearchResult } from './types.js';


/**
 * Create Anthropic-native codebase_search tool with execute and formatResult methods
 * 
 * @param config - Configuration with repoId
 * @returns Anthropic Tool definition with execute and formatResult methods
 * 
 * @example
 * ```ts
 * import Anthropic from '@anthropic-ai/sdk';
 * import { createCodebaseSearchTool } from 'morphsdk/tools/anthropic';
 * 
 * const client = new Anthropic({ apiKey: process.env.ANTHROPIC_API_KEY });
 * const tool = createCodebaseSearchTool({ repoId: 'my-project' });
 * 
 * const response = await client.messages.create({
 *   model: "claude-sonnet-4-5-20250929",
 *   tools: [tool], // tool itself is the Tool definition
 *   messages: [{ role: "user", content: "Find authentication code" }]
 * });
 * 
 * // Execute tool and format result
 * const result = await tool.execute(toolUseBlock.input);
 * const formatted = tool.formatResult(result);
 * ```
 */
export function createCodebaseSearchTool(config: CodebaseSearchConfig) {
  const toolDefinition: Tool = {
    name: 'codebase_search',
    description: CODEBASE_SEARCH_DESCRIPTION,
    input_schema: {
      type: 'object',
      properties: {
        explanation: {
          type: 'string',
          description: 'One sentence explanation as to why this tool is being used, and how it contributes to the goal.',
        },
        query: {
          type: 'string',
          description: 'A complete question about what you want to understand. Ask as if talking to a colleague: "How does X work?", "What happens when Y?", "Where is Z handled?"',
        },
        target_directories: {
          type: 'array',
          items: { type: 'string' },
          description: 'Prefix directory paths to limit search scope (single directory only, no glob patterns). Use [] to search entire repo.',
        },
        limit: {
          type: 'number',
          description: 'Maximum results to return (default: 10)',
        },
      },
      required: ['query', 'target_directories', 'explanation'],
    },
    cache_control: { type: 'ephemeral' } as any,
  } as any;

  return Object.assign(toolDefinition, {
    execute: async (input: CodebaseSearchInput): Promise<CodebaseSearchResult> => {
      return executeCodebaseSearch(input, config);
    },
    formatResult: (result: CodebaseSearchResult): string => {
      return formatResult(result);
    },
    getSystemPrompt: (): string => {
      return CODEBASE_SEARCH_SYSTEM_PROMPT;
    },
  });
}

/**
 * Format search results for Claude
 * 
 * @param result - Search result from endpoint
 * @returns Formatted string for tool_result
 */
function formatResult(result: CodebaseSearchResult): string {
  if (!result.success) {
    return `Search failed: ${result.error}`;
  }

  if (result.results.length === 0) {
    return 'No matching code found. Try rephrasing your query or broadening the search scope.';
  }

  const lines: string[] = [];
  
  lines.push(`Found ${result.results.length} relevant code sections (searched ${result.stats.candidatesRetrieved} candidates in ${result.stats.searchTimeMs}ms):\n`);

  result.results.forEach((r, i) => {
    const relevance = (r.rerankScore * 100).toFixed(1);
    lines.push(`${i + 1}. ${r.filepath} (${relevance}% relevant)`);
    lines.push(`   Symbol: ${r.symbolPath}`);
    lines.push(`   Language: ${r.language}`);
    lines.push(`   Lines: ${r.startLine}-${r.endLine}`);
    lines.push(`   Code:`);
    
    // Show code content with indentation
    const codeLines = r.content.split('\n');
    codeLines.slice(0, Math.min(codeLines.length, 20)).forEach(line => {
      lines.push(`     ${line}`);
    });
    
    if (codeLines.length > 20) {
      lines.push(`     ... (${codeLines.length - 20} more lines)`);
    }
    
    lines.push('');
  });

  return lines.join('\n');
}

